import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { AuthStore } from '@huddle-sports-platform/app-core';

import { ClubAdmin } from './club-admin';

/** Which app somebody who runs the club is currently using. */
export type AppMode = 'admin' | 'member';

const STORAGE_KEY = 'huddle-app-mode';

/**
 * Admin mode, or the member app - for people who are both.
 *
 * A club admin IS a member of the club, so they have two apps and one
 * login. The mode is which one opens: on a refresh, a cold launch, or the
 * end of any door, `Landing` sends them to the mode they last chose. An
 * admin defaults to admin mode - being sent to the member tabs and having
 * no way back was the bug this exists to fix.
 *
 * PERSISTED, because it is a preference, not session state: closing the app
 * in admin mode and reopening it in the member app would be the same
 * complaint again. Kept per device in localStorage; a login that is not an
 * admin reads as `member` no matter what is stored, so a stale preference
 * from a revoked admin cannot strand anybody on a screen they cannot use.
 *
 * The preference only survives a REFRESH. A fresh SIGN-IN resets it to
 * `admin` (see the constructor): an admin who signs in has asked for their
 * club's controls, whatever mode the device was last left in. The reset
 * happens at the auth transition, so by the time `Landing` reads
 * {@link mode} after data-init the claim has settled and the answer is
 * admin.
 *
 * It is a PREFERENCE, not a permission. Both screens are reachable by URL
 * whatever this says; `clubAdminGuard` and the Firestore rules are what
 * actually refuse.
 */
@Injectable({ providedIn: 'root' })
export class AppModeStore {
  private readonly clubAdmin = inject(ClubAdmin);
  private readonly auth = inject(AuthStore);

  private readonly chosen = signal<AppMode>(read());

  /** Auth has SETTLED (the boot restore answered) - transitions before
   *  that are the restore itself, not somebody signing in. */
  private authSettled = false;
  /** We have seen a settled signed-OUT state - the precondition that makes
   *  the next uid a live sign-in rather than a session refresh. */
  private sawSignedOut = false;

  constructor() {
    // A fresh sign-in outranks the remembered mode. Watching the auth
    // transition HERE (instead of marking every door) covers the sign-in
    // page, the sign-up fold-down's existing-account path and the reset
    // page's auto sign-in alike - and any door added later. Only a LIVE
    // signed-out -> signed-in transition counts: a refresh restores the
    // session without ever settling on signed-out, so the remembered
    // choice keeps deciding there.
    void this.auth.whenReady().then(() => {
      this.authSettled = true;
      this.sawSignedOut = !this.auth.user();
    });
    effect(() => {
      const uid = this.auth.user()?.uid ?? null;
      untracked(() => {
        if (!this.authSettled) return;
        if (uid === null) {
          this.sawSignedOut = true;
          return;
        }
        // Unconditional on purpose: the admin claim has not settled yet at
        // this moment, and `mode` already clamps non-admins to member -
        // for them this write is the documented default, not a change.
        if (this.sawSignedOut) this.set('admin');
        this.sawSignedOut = false;
      });
    });
  }

  /** The mode in force. Never `admin` for somebody who is not one. */
  readonly mode = computed<AppMode>(() => (this.clubAdmin.isAdmin() ? this.chosen() : 'member'));

  /** True when this login has a mode worth toggling. */
  readonly canSwitch = computed(() => this.clubAdmin.isAdmin());

  set(mode: AppMode): void {
    this.chosen.set(mode);
    write(mode);
  }

  /** The other one - what the Settings switch does. */
  toggle(): AppMode {
    const next: AppMode = this.mode() === 'admin' ? 'member' : 'admin';
    this.set(next);
    return next;
  }
}

/** Admin mode is the DEFAULT for an admin: it is the app they asked for. */
function read(): AppMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'member' ? 'member' : 'admin';
  } catch {
    // Private browsing, or a webview with storage disabled.
    return 'admin';
  }
}

function write(mode: AppMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // A preference that cannot be saved still holds for this session.
  }
}
